import { PositionHistory } from '@flipster/apis/v2/history/types';
import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { FeePrecision, UsdtPrecision } from '@flipster/utils/constants/currency';

import { useFeeCalculations } from './use-fee-calculations';

interface CopyTradingPositionHistory {
  grossRealizedPnl: string;
  fundingFee: string;
  tradingFee: string;
  netRealizedPnl: string;
}

interface Props {
  item: PositionHistory | CopyTradingPositionHistory;
  currency: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

function isPositionHistory(item: PositionHistory | CopyTradingPositionHistory): item is PositionHistory {
  return 'tradingFeeAqxpRedeemed' in item;
}

const PnlHistoryModal = ({ item, currency, isOpen, onClose, onExit }: Props) => {
  const feeInput = isPositionHistory(item)
    ? {
        tradingFee: item.tradingFee,
        fundingFee: item.fundingFee,
        tradingFeeAqxpRedeemed: item.tradingFeeAqxpRedeemed,
        tradingFeeVoucherRedeemed: item.tradingFeeVoucherRedeemed,
        fundingFeeAqxpRedeemed: item.fundingFeeAqxpRedeemed,
      }
    : { tradingFee: item.tradingFee, fundingFee: item.fundingFee };

  const {
    feeCalculations,
    hasTradingFeeBonusRedemption,
    tradingFeeBonusRedemptionText,
    hasFundingFeeBonusRedemption,
    fundingFeeBonusRedemptionText,
  } = useFeeCalculations(feeInput);

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} mode='bottomSheet'>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Autogen_common.r251126.s_Realized_PnL' />} />
      </Modal.Header>
      <Modal.Content>
        <dl
          className={vstack({
            width: '100%',
            alignItems: 'normal',
            borderBlockWidth: '1px',
            borderBlockStyle: 'solid',
            borderColor: 'border.default.level-2',
            py: 16,
            gap: 4,
            '& > div': { display: 'flex', justifyContent: 'space-between' },
            '& dt': {
              textStyle: 'Text/L/Regular',
              color: 'content.default.level-2',
            },
            '& dd': {
              textAlign: 'right',
            },
          })}
        >
          <div>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Trade_PnL' />
            </dt>
            <dd>
              <Formatted.Number
                value={item.grossRealizedPnl}
                precision={UsdtPrecision}
                suffix={currency}
                toFixed
                withColor
                withPlus
              />
            </dd>
          </div>
          <div>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Funding_fee' />
            </dt>
            <dd>
              {hasFundingFeeBonusRedemption && (
                <div className={hstack({ gap: 4, justify: 'flex-end' })}>
                  <Formatted.Number
                    value={feeCalculations.originalFundingFee}
                    precision={FeePrecision}
                    suffix={currency}
                    zeroString='0.00'
                    toFixed
                    css={originalFeeValueStyles}
                  />
                  <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
                </div>
              )}
              <Formatted.Number
                value={feeCalculations.actualFundingFee}
                precision={FeePrecision}
                suffix={currency}
                zeroString='0.00'
                toFixed
                withColor
                withPlus
              />
              {fundingFeeBonusRedemptionText && (
                <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                  {fundingFeeBonusRedemptionText}
                </p>
              )}
            </dd>
          </div>
          <div>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Trading_fee' />
            </dt>
            <dd>
              {hasTradingFeeBonusRedemption && (
                <div className={hstack({ gap: 4, justify: 'flex-end' })}>
                  <Formatted.Number
                    value={feeCalculations.originalTradingFee}
                    precision={FeePrecision}
                    suffix={currency}
                    zeroString='0.00'
                    toFixed
                    css={originalFeeValueStyles}
                  />
                  <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
                </div>
              )}
              <Formatted.Number
                value={feeCalculations.actualTradingFee}
                precision={FeePrecision}
                suffix={currency}
                zeroString='0.00'
                toFixed
                withColor
                withPlus
                css={actualExecFeeValueStyles}
              />
              {tradingFeeBonusRedemptionText && (
                <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                  {tradingFeeBonusRedemptionText}
                </p>
              )}
            </dd>
          </div>
        </dl>

        <dl className={flex({ gap: 16, justifyContent: 'space-between', mt: 24 })}>
          <dt className={css({ textStyle: 'Title/XS', color: 'content.default.level-5' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Total' />
          </dt>
          <dd>
            <Formatted.Number
              value={item.netRealizedPnl}
              precision={UsdtPrecision}
              suffix={currency}
              toFixed
              withColor
              withPlus
            />
          </dd>
        </dl>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button label={<FormattedMessage id='Common.r250918.Got_it' />} onClick={onClose} />
      </Modal.Footer>
    </Modal.Root>
  );
};

export default PnlHistoryModal;

export const originalFeeValueStyles: Styles = {
  textStyle: 'Text/M/Monospace/Regular',
  color: 'content.default.level-3',
  textDecorationLine: 'line-through',
};

export const actualExecFeeValueStyles: Styles = {
  textStyle: 'Text/M/Monospace/Regular',
};
